import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, map, switchMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { FileUploadResults } from 'src/app/core/models/file-upload-results.model';
import { InterceptSummary } from 'src/app/core/models/position-operations/intercepts.model';
import { RevenueRecoveryRequest } from 'src/app/core/models/revenue-recovery/revenue-recovery';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { UnitConfigurationService } from 'src/app/core/services/common/unit-configuration/unit-configuration.service';
import { TableFilterUtilsService } from 'src/app/core/utils/table-filter/table-filter-utils.service';
import {
  FileParseType,
  FileReturn,
  ParsedFileData,
} from '../../file-upload/file-upload.constants';
import { Intercept } from '../../intercept';
import { BUTTON_CLICK } from '../../modal/exit-modal/exit-modal.constants';
import {
  IMAGE_SRC,
  NO_RESULTS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import { PtnRangePackageValidator, dimensionValidator } from '../../validators';
import { AddNewScanComponent } from '../components/add-new-scan/add-new-scan.component';
import {
  CONST,
  ColumnIds,
  ColumnText,
  SORT_OPTIONS,
  SORT_OPTION_LIST,
  SizeAudit,
} from './size-audit-table.constants';

@Component({
  selector: 'app-size-audit-table',
  templateUrl: './size-audit-table.component.html',
  styleUrls: ['./size-audit-table.component.scss'],
  standalone: false,
})
export class SizeAuditTableComponent extends Intercept implements OnInit {
  @ViewChild('addNewScan') addNewScanComponent!: AddNewScanComponent;

  private readonly destroy = inject(DestroyRef);

  readonly SRC = IMAGE_SRC;

  readonly tableData = signal<SizeAudit[]>([]);
  private readonly fb = inject(FormBuilder);
  private userService = inject(UserService);
  private locationService = inject(LocationService);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly sortOption = SORT_OPTIONS;
  readonly SORT_OPTION_LIST = SORT_OPTION_LIST;
  readonly PARSE_TYPE = FileParseType;

  readonly CONST = { ...CONST, ...VALIDATION_MESSAGE };
  readonly SpecialCharacters = SpecialCharacters;

  private readonly _unitConfigurationService = inject(UnitConfigurationService);

  readonly currentLocation = signal<CurrentLocation | undefined>(undefined);
  readonly currentUser = signal<UserAdmin | undefined>(undefined);

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  trackingNumber = this.createTableColumn(ColumnIds.TRACKING_NUMBER);
  sortDate = this.createTableColumn(ColumnIds.SORT_DATE);
  sortCode = this.createTableColumn(ColumnIds.SORT_CODE);
  length = this.createTableColumn(ColumnIds.LENGTH);
  width = this.createTableColumn(ColumnIds.WIDTH);
  height = this.createTableColumn(ColumnIds.HEIGHT);
  udwType = this.createTableColumn(ColumnIds.UDW_TYPE);
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<SizeAudit>();
  form!: FormGroup;
  isFormSubmitted = signal(false);
  dropdownOpen = [false];
  minSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  maxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  sortCodeOption: Items[] = [];
  unitConfig = this._unitConfigurationService.getUnitMappingByType(
    this._unitConfigurationService.unitOfMeasureSystem(),
  );
  isFileUpload = false;
  fileUploadResults = signal<FileUploadResults | null>(null);
  tableMinSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  tableMaxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  enabledDates: Date[] = [];
  private readonly _tableFilterUtilsService = inject(TableFilterUtilsService);

  /**
   * OnInit Lifecycle hook
   *
   * @memberof SizeAuditTableComponent
   */
  ngOnInit(): void {
    this.form = this.fb.group({
      sortDate: [new Date(), Validators.required],
      sortCode: [null, Validators.required],
      trackingNumbers: [
        '',
        Validators.compose([
          Validators.required,
          PtnRangePackageValidator.trackingNumberValidator(),
        ]),
      ],
      lengthInput: ['1.0', Validators.required],
      widthInput: ['1.0', Validators.required],
      heightInput: ['1.0', Validators.required],
      palletCheckBox: [false],
    });

    this.dataSource.filterPredicate =
      this._tableFilterUtilsService.createCustomFilter('sortDate');
    this.formValueChanges();
    this.getCurrentUser();
    this.setSortCodeOption();
    this.getCurrentLocation();
  }

  formValueChanges() {
    this.form
      .get('palletCheckBox')
      ?.valueChanges.pipe(
        filter((v) => v !== null),
        takeUntilDestroyed(this.destroy),
      )
      .subscribe(() => {
        const isFormSubmitted = this.isFormSubmitted();
        if (isFormSubmitted) {
          this.isFormSubmitted.set(false);
          this.form.setValidators(null);
          this.form.updateValueAndValidity();
        }
      });
  }
  /**
   * get current user
   *
   * @memberof HandlingScanTableComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user && !!Object.keys(user).length),
        takeUntilDestroyed(this.destroy),
        switchMap((_user) => {
          this.currentUser.set(_user);
          return this.getIntercepts();
        }),
      )
      .subscribe();
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        this.currentLocation.set(val);
        if (val.location?.locationDetails.country.unit) {
          this._unitConfigurationService.unitOfMeasureSystem.set(
            val.location?.locationDetails.country.unit,
          );
        }
        this.unitConfig = this._unitConfigurationService.getUnitMappingByType(
          this._unitConfigurationService.unitOfMeasureSystem(),
        );
        this.resetState();
      });
  }

  resetState() {
    this.tableData.set([]);
    this.dataSource.data = [];
    this.form.setValidators(null);
    this.form.updateValueAndValidity();
    this.form.reset({
      sortDate: new Date(),
      lengthInput: '1.0',
      widthInput: '1.0',
      heightInput: '1.0',
    });
  }

  setSortCodeOption() {
    this.sortCodeOption = [];
    this.dataSource.data.forEach((item) => {
      const isSortCodeAdded = this.sortCodeOption.find(
        (i) => i.id === item.sortCode,
      );
      if (!isSortCodeAdded) {
        this.sortCodeOption.push({
          id: item.sortCode,
          selected: true,
        });
      }
    });
  }

  setDateRangeValidation() {
    const dates = this.dataSource.data.map((item) =>
      new Date(item.sortDate).getTime(),
    );
    // Find min date
    this.tableMinSortDate = new Date(Math.min(...dates));

    // Find max date
    this.tableMaxSortDate = new Date(Math.max(...dates));

    // enabled dates
    this.enabledDates = dates.map((d) => new Date(d));
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof SizeAuditTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    if (field.itemId === ColumnIds.SORT_CODE) {
      this.sortCode.formControl.setValue($event);
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof SizeAuditTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [
        this.trackingNumber,
        this.sortDate,
        this.length,
        this.width,
        this.height,
        this.udwType,
      ],
      this.dataSource,
      [this.sortCode],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof InboundScanTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.tableData(),
      [this.trackingNumber.formControl, this.sortDate.formControl],
      [this.sortCode],
    );
    this.resetSelectDropdowns.next(true);
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof InboundScanTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumber.formControl.value !== '' ||
      this.sortDate.formControl.value !== '' ||
      this.sortCode.ids.length > 0
    );
  }

  formatValue(value: string): string {
    const numValue = Number(value);
    return value.includes('.') ? value : numValue.toFixed(1);
  }

  setDimensionValidation() {
    if (
      this.currentLocation()?.location?.configurations.gslConfigurations
        .revenueRecoveryDetails
    ) {
      const configForPackage =
        this.currentLocation()?.location?.configurations.gslConfigurations
          .revenueRecoveryDetails.revRecoveryPackage;
      const configForPallet =
        this.currentLocation()?.location?.configurations.gslConfigurations
          .revenueRecoveryDetails.revRecoveryPallet;

      const revRecoveryCfgs = this.form.get('palletCheckBox')?.value
        ? configForPallet
        : configForPackage;
      const maxDimension = revRecoveryCfgs?.maxDimension as number;
      const minDimension = revRecoveryCfgs?.minDimension as number;

      this.form.setValidators(dimensionValidator(minDimension, maxDimension));
      this.form.updateValueAndValidity();
    }
  }

  warningPopup(sizeAudit: FileReturn, message: string) {
    const body = message;
    this.commonUtils
      .openWarningModal({
        header: CONST.WARNING_MODAL.HEADER,
        body: body,
        confirmButtonText: CONST.WARNING_MODAL.CONFIRM_BUTTON,
        rejectButtonText: CONST.WARNING_MODAL.REJECT_BUTTON,
        extraClass: 'large-confirm-btn',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.button === BUTTON_CLICK.ACCEPT) {
          this.submit(sizeAudit, true);
        }
      });
  }

  submit(fileData: FileReturn, isFileUpload: boolean) {
    this.isFileUpload = isFileUpload;

    const trackingNumber = fileData.returnData
      .flatMap((val) => val)
      .map((val) => val.trackingNumber);

    const intercept = this.intercepts().filter((item) =>
      trackingNumber.includes(item.trackingNumber),
    );

    if (intercept.length > 0) {
      this.commonUtils
        .openPackageInterceptPopup(
          intercept.map((val) => val.trackingNumber).toString(),
          !!intercept.find((val) => val.interceptType === 'LOC'),
        )
        .afterClosed()
        .subscribe((resp) => {
          if (resp === 'yes') {
            this.upload(fileData);
          } else {
            this.form.reset({
              sortDate: this.commonUtils.getDateFromString(
                this.form.get('sortDate')?.value,
              ),
              sortCode: this.form.get('sortCode')?.value,
            });
          }
        });
    } else {
      this.upload(fileData);
    }
  }

  private upload(fileData: FileReturn) {
    if (this.isFileUpload) {
      this.fileUpload(fileData);
    } else {
      this.manualUpload(fileData);
    }
  }

  private manualUpload(fileData: FileReturn, maxSizeExceedBypass = false) {
    this.commonUtils.updateLoadingModal(true);

    this.setDimensionValidation();

    if (!this.form.invalid) {
      this.form.setValidators(null);
      this.form.updateValueAndValidity();

      this.isFormSubmitted.set(true);

      const trackingNumber = fileData.returnData
        .flatMap((val) => val)
        .map((val) => val.trackingNumber);

      const intercepts = this.intercepts().filter(
        (item) => item.trackingNumber === trackingNumber[0],
      );

      const isLocalIntercept = intercepts.some((val: InterceptSummary) => {
        return val.interceptType === 'LOC';
      });

      const { lengthInput, widthInput, heightInput } = this.form.value;

      let revenueRecoveryRequest = {
        sortDate: formatDate(
          this.form.get('sortDate')?.value,
          'yyyy-MM-dd',
          'en-US',
        ),
        sortCode: this.form.get('sortCode')?.value,
        trackingNumber: trackingNumber[0],
        sizeAudit: {
          length: this.form.value.lengthInput,
          width: this.form.value.widthInput,
          height: this.form.value.heightInput,
          pallet: !!this.form.value.palletCheckBox,
          sizeAudit: maxSizeExceedBypass,
        },
        intercept: !!intercepts && !isLocalIntercept,
        localIntercept: isLocalIntercept,
        weightAudit: null,
        wWEF: null,
        length: this.formatValue(lengthInput),
        width: this.formatValue(widthInput),
        height: this.formatValue(heightInput),
      };

      if (intercepts.length > 0 && !maxSizeExceedBypass) {
        this.commonUtils
          .openPackageInterceptPopup(
            intercepts.map((val) => val.trackingNumber).toString(),
            isLocalIntercept,
          )
          .afterClosed()
          .subscribe(() => {
            this.callManualFileUpload(
              revenueRecoveryRequest,
              fileData,
              trackingNumber,
            );
          });
      } else {
        this.callManualFileUpload(
          revenueRecoveryRequest,
          fileData,
          trackingNumber,
        );
      }
    }
  }

  private fileUpload(fileData: FileReturn) {
    this.commonUtils.updateLoadingModal(true);

    const apis$ = fileData.returnData.map((val) => {
      const request = {
        additionalHandling: [],
        sortDate: formatDate(this.form.value.sortDate, 'yyyy-MM-dd', 'en-US'),
        sortCode: this.form.value.sortCode,
        sizeAudit: this.transformData(val),
        intercept: false,
        localIntercept: false,
        weightAudit: null,
        wWEF: null,
      };
      return {
        numbers: this.transformData(val).map(
          (val) => val.trackingNumber,
        ) as string[],
        call$: this._revenueRecoveryService
          .revenueRecoveryFileUpload(request, 1)
          .pipe(take(1)),
      };
    });

    this.commonUtils
      .runFileUploadCallsInChunks(
        apis$,
        this.commonUtils.generateUploadString(
          new Date(),
          this.currentLocation()?.location?.locationDetails.code || '',
          this.currentUser()?.loginId || '',
        ),
        fileData.fileName,
        fileData.fileSize,
      )
      .subscribe((succeeded) => {
        if (succeeded) {
          this.form.reset({
            sortDate: this.commonUtils.getDateFromString(
              formatDate(this.form.value.sortDate, 'yyyy-MM-dd', 'en-US'),
            ),
            lengthInput: '1.0',
            widthInput: '1.0',
            heightInput: '1.0',
          });

          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
      });
  }

  private callManualFileUpload(
    request: RevenueRecoveryRequest,
    fileData: FileReturn,
    trackingNumber: string[],
  ) {
    const { lengthInput, widthInput, heightInput } = this.form.value;

    this._revenueRecoveryService
      .getRevenueRecovery(request, 1)
      .pipe(
        take(1),
        map((response) => {
          if (response?.status === 'WARNING') {
            this.warningPopup(fileData, response.message);
            return;
          }

          if (response.code === 200) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);

            const index = this.tableData().findIndex(
              (i) => i.trackingNumber === trackingNumber[0],
            );

            const formData = {
              sortDate: this.form.get('sortDate')?.value,
              sortCode: this.form.get('sortCode')?.value,
              length: lengthInput,
              width: widthInput,
              height: heightInput,
              udwType: this.form.get('udwType')?.value,
              trackingNumber: trackingNumber[0],
            };

            if (index > -1) {
              this.tableData.update((current) => {
                current[index] = formData;
                return [...current];
              });
            } else {
              this.tableData.update((current) => {
                return [{ ...formData }, ...current];
              });
            }
            this.dataSource.data = this.tableData();
            this.setSortCodeOption();
            this.setDateRangeValidation();
            this.form.reset({
              sortDate: this.commonUtils.getDateFromString(request.sortDate),
              lengthInput: '1.0',
              widthInput: '1.0',
              heightInput: '1.0',
            });
          }
        }),
      )
      .subscribe();
  }

  private transformData(data: ParsedFileData[]): any[] {
    // Ensure at least 6 elements by adding empty strings for missing values
    return data.map((val) => ({
      trackingNumber: val.trackingNumber,
      length: val.length ?? '',
      width: val.width ?? '',
      height: val.height ?? '',
      unit: val.unit ?? '',
      uow: val.uow ?? '',
    }));
  }
}
